import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getErrorMessage } from "@/lib/errors";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { z } from "zod";
import { Plus, Check, Clock, Repeat, CalendarIcon, AlertCircle, ChevronsUpDown, ArrowRightLeft } from "lucide-react";
import { cn } from "@/lib/utils";
import { format, parse, isValid } from "date-fns";
import { parseLocalDate, toISODate, todayISO } from "@/lib/dates";

const DEFAULT_INCOME_CATEGORIES = ["Salary", "Freelance", "Investment", "Gift", "Other"];
const DEFAULT_EXPENSE_CATEGORIES = [
  "Housing",
  "Transportation",
  "Food",
  "Utilities",
  "Healthcare",
  "Entertainment",
  "Shopping",
  "Education",
  "Savings",
  "Other",
];

interface RecurringTransaction {
  id: string;
  type: "income" | "expense";
  amount: number;
  category: string;
  account: string;
  description: string | null;
  frequency: string;
}

const transactionSchema = z.object({
  type: z.enum(["income", "expense"]),
  amount: z.number().positive({ message: "Amount must be greater than 0" }).max(1000000000, { message: "Amount too large" }),
  category: z.string().trim().min(1, { message: "Category is required" }).max(100),
  account: z.string().trim().min(1, { message: "Account is required" }).max(100),
  description: z.string().max(500, { message: "Description must be less than 500 characters" }).optional().nullable(),
  date: z.string().min(1, { message: "Date is required" }),
});

interface TransactionAddDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  onSwitchToTransfer?: () => void;
}

export const TransactionAddDialog = ({
  open,
  onOpenChange,
  onSuccess,
  onSwitchToTransfer,
}: TransactionAddDialogProps) => {
  const [type, setType] = useState<"income" | "expense">("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("");
  const [categoryFocused, setCategoryFocused] = useState(false);
  const [customCategories, setCustomCategories] = useState<{ income: string[]; expense: string[] }>({ income: [], expense: [] });
  const [showNewCategoryConfirm, setShowNewCategoryConfirm] = useState(false);
  const [pendingCategoryName, setPendingCategoryName] = useState("");
  const [fundingSourceOptions, setFundingSourceOptions] = useState<Map<string, string>>(new Map());
  const [account, setAccount] = useState("");
  const [accountFocused, setAccountFocused] = useState(false);
  const [description, setDescription] = useState("");
  const [dateInput, setDateInput] = useState("");
  const [date, setDate] = useState(todayISO());
  const [dateCalendarOpen, setDateCalendarOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [paymentSources, setPaymentSources] = useState<{ value: string; label: string; isWallet?: boolean; parentAccount?: string; isFundingOption?: boolean; isPrimary?: boolean }[]>([]);
  const [addedCount, setAddedCount] = useState(0);
  const [onboardingDate, setOnboardingDate] = useState<string | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringMode, setRecurringMode] = useState<"existing" | "new">("existing");
  const [existingRecurring, setExistingRecurring] = useState<RecurringTransaction[]>([]);
  const [selectedRecurringId, setSelectedRecurringId] = useState<string>("");
  const [frequency, setFrequency] = useState("monthly");
  const [holdDate, setHoldDate] = useState(false);
  const [accounts, setAccounts] = useState<any[]>([]); // Store accounts data for account ID lookup
  const { toast } = useToast();
  const { parseNumber, decimalSeparator, getPlaceholder, preferences } = useUserPreferences();

  // Refs for managing focus
  const categoryInputRef = useRef<HTMLInputElement>(null);
  const accountInputRef = useRef<HTMLInputElement>(null);

  // Get locale-based date format
  const getDateFormat = () => {
    const locale = preferences?.locale || "en-US";
    // Common locale date formats
    if (locale.startsWith("en-US")) return "MM/dd/yyyy";
    if (locale.startsWith("en-GB") || locale.startsWith("de") || locale.startsWith("fr") || locale.startsWith("es") || locale.startsWith("it")) return "dd/MM/yyyy";
    if (locale.startsWith("ja") || locale.startsWith("zh") || locale.startsWith("ko")) return "yyyy/MM/dd";
    return "dd/MM/yyyy"; // Default
  };

  const dateFormat = getDateFormat();

  // Parse user-input date string to ISO format
  const parseDateInput = (input: string): string | null => {
    if (!input.trim()) return null;
    try {
      const parsed = parse(input, dateFormat, new Date());
      if (isValid(parsed)) {
        return format(parsed, "yyyy-MM-dd");
      }
    } catch {
      // Try ISO format as fallback
      try {
        const parsed = new Date(input);
        if (isValid(parsed)) {
          return format(parsed, "yyyy-MM-dd");
        }
      } catch {
        return null;
      }
    }
    return null;
  };

  // Format ISO date to locale display
  const formatDateForDisplay = (isoDate: string): string => {
    try {
      const d = parseLocalDate(isoDate);
      if (isValid(d)) {
        return format(d, dateFormat);
      }
    } catch {
      // invalid date — fall back to the raw ISO string
    }
    return isoDate;
  };

  // Merge default and custom categories, removing duplicates
  const allCategories = useMemo(() => {
    const defaults = type === "income" ? DEFAULT_INCOME_CATEGORIES : DEFAULT_EXPENSE_CATEGORIES;
    const custom = type === "income" ? customCategories.income : customCategories.expense;
    const merged = [...new Set([...defaults, ...custom])];
    return merged.sort((a, b) => a.localeCompare(b));
  }, [type, customCategories]);

  // Filter categories based on current input
  const filteredCategories = useMemo(() => {
    if (!category.trim()) return allCategories;
    return allCategories.filter(cat => 
      cat.toLowerCase().includes(category.toLowerCase())
    );
  }, [allCategories, category]);

  // Check if current input is a new category
  const isNewCategory = useMemo(() => {
    if (!category.trim()) return false;
    return !allCategories.some(cat => cat.toLowerCase() === category.toLowerCase());
  }, [allCategories, category]);

  // Check if category is valid (exists in the list)
  const isCategoryValid = useMemo(() => {
    if (!category.trim()) return false;
    return allCategories.some(cat => cat.toLowerCase() === category.toLowerCase());
  }, [allCategories, category]);

  // Filter payment sources based on current input
  const filteredPaymentSources = useMemo(() => {
    if (!account.trim()) return paymentSources;
    const search = account.toLowerCase();
    return paymentSources.filter(source => 
      source.value.toLowerCase().includes(search) || 
      source.label.toLowerCase().includes(search)
    );
  }, [paymentSources, account]);

  // Check if account is valid (exists in the list)
  const isAccountValid = useMemo(() => {
    if (!account.trim()) return false;
    return paymentSources.some(s => s.value === account);
  }, [paymentSources, account]);

  // Get account ID for a given account value
  const getAccountId = useCallback((accountValue: string): string | null => {
    // Guard against null/undefined accounts array
    if (!accounts || !Array.isArray(accounts)) {
      return null;
    }

    // Direct account match
    const directMatch = paymentSources.find(s => s.value === accountValue && !s.isWallet && !s.isFundingOption);
    if (directMatch) {
      // Find the actual account ID from the stored accounts data
      const accountEntry = accounts.find(acc => acc.name === directMatch.label);
      return accountEntry ? accountEntry.id : null;
    }

    // Wallet match
    const walletMatch = paymentSources.find(s => s.value === accountValue && s.isWallet);
    if (walletMatch) {
      // Find the parent account ID
      const parentAccount = accounts.find(acc => acc.name === walletMatch.parentAccount);
      return parentAccount ? parentAccount.id : null;
    }

    // Funding option match
    const fundingMatch = paymentSources.find(s => s.value === accountValue && s.isFundingOption);
    if (fundingMatch && fundingMatch.parentAccount) {
      // Find the funding account ID
      const fundingAccount = accounts.find(acc => acc.name === fundingMatch.parentAccount);
      return fundingAccount ? fundingAccount.id : null;
    }

    return null;
  }, [paymentSources, accounts]);

  const selectAccount = (value: string) => {
    setAccount(value);
    setAccountFocused(false);
    accountInputRef.current?.blur();
  };

  const selectCategory = (cat: string) => {
    setCategory(cat);
    setCategoryFocused(false);
    categoryInputRef.current?.blur();
  };

  // Show confirmation before adding new category
  const promptNewCategory = () => {
    if (category.trim() && isNewCategory) {
      setPendingCategoryName(category.trim());
      setShowNewCategoryConfirm(true);
      setCategoryFocused(false);
    }
  };

  const confirmAddNewCategory = async () => {
    if (!pendingCategoryName) return;
    
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("categories").insert({
          user_id: user.id,
          name: pendingCategoryName,
          type: type,
        });
        
        setCustomCategories(prev => ({
          ...prev,
          [type]: [...prev[type], pendingCategoryName]
        }));
        
        setCategory(pendingCategoryName);
        toast({
          title: "Category Added",
          description: `"${pendingCategoryName}" added to ${type} categories`,
        });
      }
    } catch (error) {
      console.log("Category may already exist:", error);
    }
    
    setShowNewCategoryConfirm(false);
    setPendingCategoryName("");
    categoryInputRef.current?.blur();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch accounts, wallets, preferences, categories table, existing transactions, and funding sources
        const [accountsRes, walletsRes, prefsRes, categoriesRes, transactionsRes, fundingSourcesRes] = await Promise.all([
          supabase.from("accounts").select("id, name").order("created_at", { ascending: true }),
          supabase.from("wallets").select("id, account_id, name").order("created_at", { ascending: true }),
          supabase.from("user_preferences").select("onboarding_completed_at, default_account").single(),
          supabase.from("categories").select("name, type"),
          supabase.from("transactions").select("type, category"),
          supabase.from("account_funding_sources").select("*").order('account_id').order('priority'),
        ]);

        if (accountsRes.error) throw accountsRes.error;

        const accounts = accountsRes.data || [];
        setAccounts(accounts); // Store accounts for account ID lookup

        // Fetch existing recurring transactions
        const { data: recurringData } = await supabase
          .from("recurring_transactions")
          .select("id, type, amount, category, account, description, frequency")
          .eq("is_active", true)
          .order("category", { ascending: true });

        setExistingRecurring((recurringData || []) as RecurringTransaction[]);
        const wallets = walletsRes.data || [];
        const fundingSources = fundingSourcesRes.data || [];

        // Build payment sources list with accounts, their ranked funding options, and wallets
        const sources: { value: string; label: string; isWallet?: boolean; parentAccount?: string; isFundingOption?: boolean; isPrimary?: boolean }[] = [];
        const fundingOptions = new Map<string, string>(); // passThruValue -> fundingAccountName

        if (accounts && Array.isArray(accounts)) {
          accounts.forEach(acc => {
            // Add the account itself (direct - uses own balance)
            sources.push({ value: acc.name, label: acc.name });

            // Add a pass-through option for each ranked funding source
            const accFundingSources = fundingSources
              .filter(fs => fs.account_id === acc.id)
              .sort((a, b) => a.priority - b.priority);

            accFundingSources.forEach(fs => {
              const fundingAcc = accounts.find(a => a.id === fs.funding_account_id);
              if (fundingAcc) {
                const passThruValue = `${acc.name} ⇢ ${fundingAcc.name}`;
                sources.push({
                  value: passThruValue,
                  label: fs.label || acc.name,
                  isFundingOption: true,
                  parentAccount: fundingAcc.name,
                  isPrimary: fs.priority === 1,
                });
                fundingOptions.set(passThruValue, fundingAcc.name);
              }
            });

            // Add wallets under this account
            const accountWallets = wallets.filter(w => w.account_id === acc.id);
            accountWallets.forEach(wallet => {
              sources.push({
                value: `${acc.name} → ${wallet.name}`,
                label: wallet.name,
                isWallet: true,
                parentAccount: acc.name,
              });
            });
          });
        }

        setPaymentSources(sources);
        setFundingSourceOptions(fundingOptions);

        // Set default account: respect saved preference; otherwise auto-select primary funding option
        if (sources.length > 0) {
          const defaultAcc = prefsRes.data?.default_account;
          if (defaultAcc && sources.some(s => s.value === defaultAcc)) {
            setAccount(defaultAcc);
          } else {
            // Use first direct account; if it has a primary funding source, prefer that
            const firstDirect = sources.find(s => !s.isWallet && !s.isFundingOption);
            if (firstDirect) {
              const primaryFunding = sources.find(s => s.isFundingOption && s.isPrimary && s.label === firstDirect.label);
              setAccount(primaryFunding ? primaryFunding.value : firstDirect.value);
            } else {
              setAccount(sources[0].value);
            }
          }
        }

        if (prefsRes.data?.onboarding_completed_at) {
          setOnboardingDate(prefsRes.data.onboarding_completed_at);
        }

        // Combine categories from categories table and transactions (for backward compat)
        const incomeCategoriesFromDb = new Set<string>();
        const expenseCategoriesFromDb = new Set<string>();

        // Add from categories table
        if (categoriesRes.data) {
          categoriesRes.data.forEach(c => {
            if (c.type === "income") {
              incomeCategoriesFromDb.add(c.name);
            } else {
              expenseCategoriesFromDb.add(c.name);
            }
          });
        }

        // Add from existing transactions (for backward compatibility)
        if (transactionsRes.data) {
          transactionsRes.data.forEach(t => {
            if (t.type === "income" && t.category) {
              incomeCategoriesFromDb.add(t.category);
            } else if (t.type === "expense" && t.category) {
              expenseCategoriesFromDb.add(t.category);
            }
          });
        }

        setCustomCategories({
          income: [...incomeCategoriesFromDb],
          expense: [...expenseCategoriesFromDb]
        });
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    if (open) {
      fetchData();
      // Reset form when opening
      resetForm(false);
      setAddedCount(0);
      setHoldDate(false);
    }
  }, [open]);

  const resetForm = (keepDate: boolean = false) => {
    setAmount("");
    setCategory("");
    // Keep account for faster entry
    if (!keepDate) {
      setDateInput("");
      setDate(todayISO());
    }
    setDescription("");
    setIsRecurring(false);
    setRecurringMode("existing");
    setSelectedRecurringId("");
    setFrequency("monthly");
  };

  // Filter recurring transactions by current type
  const filteredRecurring = useMemo(() => {
    return existingRecurring.filter(r => r.type === type);
  }, [existingRecurring, type]);

  // Handle selecting an existing recurring transaction
  const handleSelectRecurring = (recurringId: string) => {
    setSelectedRecurringId(recurringId);
    const recurring = existingRecurring.find(r => r.id === recurringId);
    if (recurring) {
      setAmount(recurring.amount.toString());
      setCategory(recurring.category);
      setAccount(recurring.account);
      setDescription(recurring.description || "");
      setFrequency(recurring.frequency);
    }
  };

  // Check if transaction is backdated (before onboarding)
  const isBackdated = () => {
    if (!onboardingDate) return false;
    const transactionDate = parseLocalDate(date);
    const onboardingDateObj = new Date(onboardingDate);
    // Set to start of day for comparison
    transactionDate.setHours(0, 0, 0, 0);
    onboardingDateObj.setHours(0, 0, 0, 0);
    return transactionDate < onboardingDateObj;
  };

  const saveTransaction = async () => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("Not authenticated");

    // Validate account exists
    if (!isAccountValid) {
      throw new Error("Please select a valid account");
    }

    // Validate input
    const validatedData = transactionSchema.parse({
      type,
      amount: parseNumber(amount),
      category,
      account,
      description: description.trim() || null,
      date,
    });

    // Get account ID for the selected account
    const accountId = getAccountId(account);
    if (!accountId) {
      throw new Error("Unable to find account ID for selected account");
    }

    const backdated = isBackdated();

    // Check if this is a funding source pass-through option (e.g., "PayPal ⇢ Sparkasse")
    // If so, we still use the same account ID since the mapping is handled in getAccountId
    const actualAccountId = accountId;

    // Require category to already exist - user must explicitly add new categories first
    const isNewCat = !allCategories.some(cat => cat.toLowerCase() === validatedData.category.toLowerCase());
    if (isNewCat) {
      throw new Error(`Category "${validatedData.category}" doesn't exist. Please add it as a new category first by clicking "Add as new category" in the dropdown.`);
    }

    const { error } = await supabase.from("transactions").insert({
      user_id: user.id,
      type: validatedData.type,
      amount: validatedData.amount,
      category: validatedData.category,
      account_id: actualAccountId,
      description: validatedData.description,
      date: validatedData.date,
      is_backdated: backdated,
    });

    if (error) throw error;

    // Also create recurring transaction if requested (only for "new" mode)
    if (isRecurring && recurringMode === "new") {
      const { error: recurringError } = await supabase.from("recurring_transactions").insert({
        user_id: user.id,
        type: validatedData.type,
        amount: validatedData.amount,
        category: validatedData.category,
        account_id: actualAccountId,
        description: validatedData.description,
        frequency,
        next_occurrence: getNextOccurrence(validatedData.date, frequency),
      });

      if (recurringError) throw recurringError;
    }

    return { ...validatedData, is_backdated: backdated, isRecurring: isRecurring && recurringMode === "new" };
  };

  const getNextOccurrence = (startDate: string, freq: string): string => {
    const date = parseLocalDate(startDate);
    switch (freq) {
      case "daily":
        date.setDate(date.getDate() + 1);
        break;
      case "weekly":
        date.setDate(date.getDate() + 7);
        break;
      case "monthly":
        date.setMonth(date.getMonth() + 1);
        break;
      case "yearly":
        date.setFullYear(date.getFullYear() + 1);
        break;
    }
    return toISODate(date);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await saveTransaction();

      toast({
        title: "Success",
        description: (isRecurring && recurringMode === "new") ? "Transaction added and set as recurring" : "Transaction added successfully",
      });

      onOpenChange(false);
      onSuccess();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAndAddAnother = async () => {
    setLoading(true);

    try {
      const saved = await saveTransaction();

      setAddedCount(prev => prev + 1);
      
      toast({
        title: `Transaction ${addedCount + 1} added! ✓`,
        description: `${saved.type === 'income' ? '+' : '-'}${saved.amount} ${saved.category}`,
      });

      // Reset form but keep type, account, and optionally date for faster entry
      resetForm(holdDate);
      onSuccess();
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation Error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Error",
          description: getErrorMessage(error),
          variant: "destructive",
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle>Add Transaction</DialogTitle>
            {addedCount > 0 && (
              <Badge variant="secondary" className="gap-1">
                <Check className="h-3 w-3" />
                {addedCount} added
              </Badge>
            )}
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Type</Label>
            <Select value={type} onValueChange={(v: "income" | "expense") => {
              setType(v);
              // Reset recurring selection when type changes
              setSelectedRecurringId("");
              setRecurringMode("existing");
            }}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="income">Income</SelectItem>
                <SelectItem value="expense">Expense</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Recurring transaction toggle */}
          <div className="space-y-3 p-3 rounded-lg bg-muted/50 border border-border/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Repeat className="h-4 w-4 text-muted-foreground" />
                <Label htmlFor="is-recurring" className="text-sm font-medium cursor-pointer">
                  Recurring Transaction
                </Label>
              </div>
              <Switch
                id="is-recurring"
                checked={isRecurring}
                onCheckedChange={(checked) => {
                  setIsRecurring(checked);
                  if (!checked) {
                    setSelectedRecurringId("");
                    setRecurringMode("existing");
                  }
                }}
              />
            </div>
            
            {isRecurring && (
              <div className="space-y-3 pt-2 border-t border-border/50">
                {filteredRecurring.length > 0 ? (
                  <>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant={recurringMode === "existing" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => setRecurringMode("existing")}
                      >
                        Select Existing
                      </Button>
                      <Button
                        type="button"
                        variant={recurringMode === "new" ? "default" : "outline"}
                        size="sm"
                        className="flex-1"
                        onClick={() => {
                          setRecurringMode("new");
                          setSelectedRecurringId("");
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Create New
                      </Button>
                    </div>
                    
                    {recurringMode === "existing" && (
                      <div className="space-y-2">
                        <Label className="text-xs text-muted-foreground">Select recurring transaction</Label>
                        <Select value={selectedRecurringId} onValueChange={handleSelectRecurring}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Choose existing recurring..." />
                          </SelectTrigger>
                          <SelectContent>
                            {filteredRecurring.map((r) => (
                              <SelectItem key={r.id} value={r.id}>
                                <div className="flex flex-col items-start">
                                  <div className="flex items-center gap-2">
                                    <span className="font-medium">{r.description || r.category}</span>
                                    <span className="text-muted-foreground">·</span>
                                    <span className="font-mono text-sm">{r.amount.toFixed(2)}</span>
                                  </div>
                                  <span className="text-xs text-muted-foreground">
                                    {r.category} · {r.frequency}
                                  </span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {selectedRecurringId && (
                          <p className="text-xs text-muted-foreground">
                            Form populated from recurring. Adjust if needed.
                          </p>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    No existing recurring {type}s found. A new recurring will be created.
                  </p>
                )}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-amount">Amount</Label>
            <Input
              id="add-amount"
              type="text"
              inputMode="decimal"
              placeholder={getPlaceholder()}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
              autoFocus
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Use "{decimalSeparator}" for decimals
            </p>
          </div>

          {/* Category - Input with dropdown */}
          <div className="space-y-2">
            <Label>Category</Label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <Input
                  ref={categoryInputRef}
                  placeholder="Type to search or add..."
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  onFocus={() => setCategoryFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setCategoryFocused(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredCategories.length > 0) {
                      e.preventDefault();
                      selectCategory(filteredCategories[0]);
                    } else if (e.key === "Escape") {
                      setCategoryFocused(false);
                      categoryInputRef.current?.blur();
                    }
                  }}
                  className={cn(
                    "pr-8",
                    category && !isCategoryValid && !isNewCategory && "border-destructive"
                  )}
                />
                {category && (isCategoryValid || isNewCategory) && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-income-text" />
                )}
                {category && !isCategoryValid && !isNewCategory && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setCategoryFocused(!categoryFocused);
                  if (!categoryFocused) {
                    categoryInputRef.current?.focus();
                  }
                }}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </div>
            {/* Suggestions dropdown */}
            {categoryFocused && (filteredCategories.length > 0 || isNewCategory) && (
              <div className="rounded-md border bg-popover p-1 shadow-md max-h-[200px] overflow-y-auto">
                {isNewCategory && category.trim() && (
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => promptNewCategory()}
                    className="flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left text-primary-text"
                  >
                    <Plus className="h-4 w-4" />
                    Add "{category.trim()}" as new category
                  </button>
                )}
                {filteredCategories.slice(0, 8).map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectCategory(cat)}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left",
                      category === cat && "bg-accent"
                    )}
                  >
                    <Check className={cn("h-4 w-4", category === cat ? "opacity-100" : "opacity-0")} />
                    {cat}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Account - Input with dropdown */}
          <div className="space-y-2">
            <Label>
              {type === "income" ? "Income Source / Account" : "Payment Account"}
            </Label>
            <div className="flex gap-1">
              <div className="relative flex-1">
                <Input
                  ref={accountInputRef}
                  placeholder="Type to search accounts..."
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  onFocus={() => setAccountFocused(true)}
                  onBlur={() => {
                    setTimeout(() => setAccountFocused(false), 150);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && filteredPaymentSources.length > 0) {
                      e.preventDefault();
                      selectAccount(filteredPaymentSources[0].value);
                    } else if (e.key === "Escape") {
                      setAccountFocused(false);
                      accountInputRef.current?.blur();
                    }
                  }}
                  className={cn(
                    "pr-8",
                    account && !isAccountValid && "border-destructive"
                  )}
                />
                {account && isAccountValid && (
                  <Check className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-income-text" />
                )}
                {account && !isAccountValid && (
                  <AlertCircle className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-destructive" />
                )}
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => {
                  setAccountFocused(!accountFocused);
                  if (!accountFocused) {
                    accountInputRef.current?.focus();
                  }
                }}
              >
                <ChevronsUpDown className="h-4 w-4" />
              </Button>
            </div>
            {/* Suggestions dropdown */}
            {accountFocused && filteredPaymentSources.length > 0 && (
              <div className="rounded-md border bg-popover p-1 shadow-md max-h-[250px] overflow-y-auto">
                {filteredPaymentSources.slice(0, 10).map((source) => (
                  <button
                    key={source.value}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectAccount(source.value)}
                    className={cn(
                      "flex items-center gap-2 w-full px-2 py-1.5 text-sm rounded-sm hover:bg-accent text-left",
                      source.isWallet && "pl-6",
                      source.isFundingOption && "pl-4 text-muted-foreground",
                      account === source.value && "bg-accent"
                    )}
                  >
                    <Check className={cn("h-4 w-4 shrink-0", account === source.value ? "opacity-100" : "opacity-0")} />
                    {source.isWallet ? (
                      <span className="flex flex-col items-start">
                        <span>{source.label}</span>
                        <span className="text-xs text-muted-foreground">in {source.parentAccount}</span>
                      </span>
                    ) : source.isFundingOption ? (
                      <span className="flex flex-col items-start">
                        <span>{source.value}</span>
                        <span className="text-xs text-muted-foreground">
                          via {source.parentAccount}{source.isPrimary ? ' · Primary' : ''}
                        </span>
                      </span>
                    ) : (
                      <span className="font-medium">{source.label}</span>
                    )}
                  </button>
                ))}
                {filteredPaymentSources.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-2">No accounts found</p>
                )}
              </div>
            )}
            {!isAccountValid && account && !accountFocused && (
              <p className="text-xs text-destructive">Please select a valid account</p>
            )}
            {fundingSourceOptions.get(account) && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <span className="text-primary-text">↳</span> 
                Will be recorded on <span className="font-medium">{fundingSourceOptions.get(account)}</span>
              </p>
            )}
          </div>

          {/* Date - Input with calendar picker */}
          <div className="space-y-2">
            <Label>Date</Label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  placeholder={dateFormat.toLowerCase()}
                  value={dateInput || formatDateForDisplay(date)}
                  onChange={(e) => {
                    setDateInput(e.target.value);
                    const parsed = parseDateInput(e.target.value);
                    if (parsed) {
                      setDate(parsed);
                    }
                  }}
                  onBlur={() => {
                    // On blur, validate and reset if invalid
                    const parsed = parseDateInput(dateInput);
                    if (parsed) {
                      setDate(parsed);
                      setDateInput("");
                    } else if (dateInput) {
                      // Invalid date, reset to current
                      setDateInput("");
                    }
                  }}
                  className={cn(
                    dateInput && !parseDateInput(dateInput) && "border-destructive"
                  )}
                />
              </div>
              <Popover open={dateCalendarOpen} onOpenChange={setDateCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="icon" type="button">
                    <CalendarIcon className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={parseLocalDate(date)}
                    defaultMonth={parseLocalDate(date)}
                    onSelect={(d) => {
                      if (d) {
                        setDate(format(d, "yyyy-MM-dd"));
                        setDateInput("");
                        setDateCalendarOpen(false);
                      }
                    }}
                    initialFocus
                    className="pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground">
                Format: {dateFormat.toLowerCase()}
              </p>
              <div className="flex items-center gap-2">
                <Switch
                  id="hold-date"
                  checked={holdDate}
                  onCheckedChange={setHoldDate}
                  className="scale-75"
                />
                <Label htmlFor="hold-date" className="text-xs text-muted-foreground cursor-pointer">
                  Hold date
                </Label>
              </div>
            </div>
            {isBackdated() && (
              <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-600 dark:text-amber-400">
                <Clock className="h-4 w-4 flex-shrink-0" />
                <p className="text-xs">
                  This is a historical transaction (before your setup date) and won't affect your balance calculations.
                </p>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-description">Description (Optional)</Label>
            <Textarea
              id="add-description"
              placeholder="Add a note..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
            />
            <p className="text-xs text-muted-foreground">
              {description.length}/500 characters
            </p>
          </div>

          {/* Recurring section - only show when creating new recurring */}
          {isRecurring && recurringMode === "new" && (
            <div className="space-y-2 p-3 rounded-lg bg-muted/50 border border-border/50">
              <Label className="text-xs text-muted-foreground">Frequency</Label>
              <Select value={frequency} onValueChange={setFrequency}>
                <SelectTrigger className="h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Next occurrence will be automatically scheduled
              </p>
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Button
              type="button"
              variant="outline"
              className="w-full gap-2 border-primary/30 hover:border-primary/50 hover:bg-primary/5"
              onClick={handleSaveAndAddAnother}
              disabled={loading}
            >
              <Plus className="h-4 w-4" />
              {loading ? "Saving..." : "Save & Add Another"}
            </Button>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="ghost"
                className="flex-1"
                onClick={() => onOpenChange(false)}
              >
                {addedCount > 0 ? "Done" : "Cancel"}
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-gradient-to-r from-primary to-secondary hover:opacity-90" 
                disabled={loading}
              >
                {loading ? "Adding..." : "Save & Close"}
              </Button>
            </div>
            {onSwitchToTransfer && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full text-muted-foreground hover:text-foreground gap-2"
                onClick={() => {
                  onOpenChange(false);
                  onSwitchToTransfer();
                }}
              >
                <ArrowRightLeft className="h-4 w-4" />
                Switch to Transfer
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
      
      {/* Category confirmation dialog */}
      <AlertDialog open={showNewCategoryConfirm} onOpenChange={setShowNewCategoryConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Create new category?</AlertDialogTitle>
            <AlertDialogDescription>
              "{pendingCategoryName}" doesn't exist yet. Would you like to add it as a new {type} category?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowNewCategoryConfirm(false);
              setPendingCategoryName("");
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmAddNewCategory}>
              Add Category
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  );
};
